import type { StrapiSamplingCheckToolSaveRow } from '../../api/strapiCrm';
import type { DynamicEntityRow } from '../../api/strapiDynamicEntity';

export const PERALATAN_KODE_PREFIX = 'PERALATAN_';

export const SAMPLING_PLAN_PERALATAN_FIELDS = [
  'peralatan_pengambilan_contoh',
  'peralatan_pengukuran_lapangan',
  'peralatan_pendukung',
  'peralatan_k3',
] as const;

export type SamplingPlanPeralatanField = (typeof SAMPLING_PLAN_PERALATAN_FIELDS)[number];

export const SAMPLING_PLAN_PERALATAN_FIELD_LABELS: Record<SamplingPlanPeralatanField, string> = {
  peralatan_pengambilan_contoh: 'Peralatan pengambilan contoh',
  peralatan_pengukuran_lapangan: 'Peralatan pengukuran lapangan',
  peralatan_pendukung: 'Peralatan pendukung',
  peralatan_k3: 'Peralatan K-3',
};

export type PeralatanReferensiGroup = {
  field: SamplingPlanPeralatanField;
  label: string;
  items: DynamicEntityRow[];
};

function rowString(row: DynamicEntityRow | Record<string, unknown> | null | undefined, key: string): string {
  return String(row?.[key] ?? '').trim();
}

export function referensiKodeKategori(row: DynamicEntityRow | null | undefined): string {
  return rowString(row, 'kode_kategori');
}

export function isPeralatanKodeKategori(kode: string): boolean {
  return kode.toUpperCase().startsWith(PERALATAN_KODE_PREFIX);
}

export function referensiOptionLabel(row: DynamicEntityRow | null | undefined): string {
  if (!row) return '';
  return [rowString(row, 'kode_kategori'), rowString(row, 'kode'), rowString(row, 'deskripsi1')]
    .filter(Boolean)
    .join(' — ') || row.documentId;
}

/** Label item di dalam grup peralatan_* (tanpa kode_kategori; grup sudah punya judul). */
export function referensiPeralatanItemLabel(row: DynamicEntityRow | null | undefined): string {
  if (!row) return '';
  const tail = [rowString(row, 'kode'), rowString(row, 'deskripsi1')].filter(Boolean).join(' — ');
  if (tail) return tail;
  const full = referensiOptionLabel(row);
  const sep = full.indexOf(' — ');
  return sep >= 0 ? full.slice(sep + 3).trim() : full;
}

function relationMany(row: DynamicEntityRow | null | undefined, field: string): DynamicEntityRow[] {
  const raw = row?.[field];
  return Array.isArray(raw) ? (raw as DynamicEntityRow[]) : [];
}

/** Referensi peralatan per grup field peralatan_* (urutan field tetap, dedupe per grup). */
export function extractPeralatanReferensiGroupsFromPlans(
  plans: DynamicEntityRow[],
): PeralatanReferensiGroup[] {
  return SAMPLING_PLAN_PERALATAN_FIELDS.map((field) => {
    const seen = new Set<string>();
    const items: DynamicEntityRow[] = [];
    for (const plan of plans) {
      for (const ref of relationMany(plan, field)) {
        if (!isPeralatanKodeKategori(referensiKodeKategori(ref))) continue;
        const id = String(ref.documentId ?? '').trim();
        if (!id || seen.has(id)) continue;
        seen.add(id);
        items.push(ref);
      }
    }
    items.sort((a, b) => referensiOptionLabel(a).localeCompare(referensiOptionLabel(b), 'id'));
    return {
      field,
      label: SAMPLING_PLAN_PERALATAN_FIELD_LABELS[field],
      items,
    };
  });
}

/** Referensi peralatan terpilih di rencana sampling (hanya kode kategori PERALATAN_*). */
export function extractPeralatanReferensiFromPlans(plans: DynamicEntityRow[]): DynamicEntityRow[] {
  const seen = new Set<string>();
  const out: DynamicEntityRow[] = [];
  for (const plan of plans) {
    for (const field of SAMPLING_PLAN_PERALATAN_FIELDS) {
      for (const ref of relationMany(plan, field)) {
        if (!isPeralatanKodeKategori(referensiKodeKategori(ref))) continue;
        const id = String(ref.documentId ?? '').trim();
        if (!id || seen.has(id)) continue;
        seen.add(id);
        out.push(ref);
      }
    }
  }
  return out.sort((a, b) => referensiOptionLabel(a).localeCompare(referensiOptionLabel(b), 'id'));
}

/** Kode kategori unik (PERALATAN_*) dari referensi terpilih di rencana sampling. */
export function kategoriKodesFromPeralatanReferensi(refs: DynamicEntityRow[]): string[] {
  const kodes = new Set<string>();
  for (const ref of refs) {
    const kode = referensiKodeKategori(ref);
    if (isPeralatanKodeKategori(kode)) kodes.add(kode);
  }
  return [...kodes];
}

export type EquipmentChecklistItem = {
  referensiDocumentId: string;
  label: string;
};

export function equipmentChecklistItemsFromPlans(plans: DynamicEntityRow[]): EquipmentChecklistItem[] {
  return extractPeralatanReferensiFromPlans(plans).map((ref) => ({
    referensiDocumentId: String(ref.documentId ?? '').trim(),
    label: referensiPeralatanItemLabel(ref),
  })).filter((item) => item.referensiDocumentId && item.label);
}

export function relationReferensiDocumentId(row: DynamicEntityRow): string {
  const ref = row.referensi;
  if (!ref || typeof ref !== 'object' || Array.isArray(ref)) return '';
  return String((ref as Record<string, unknown>).documentId ?? '').trim();
}

export function equipmentChecklistItemsFromSavedTools(rows: DynamicEntityRow[]): EquipmentChecklistItem[] {
  const seen = new Set<string>();
  const out: EquipmentChecklistItem[] = [];
  for (const row of rows) {
    const ref = row.referensi;
    if (!ref || typeof ref !== 'object' || Array.isArray(ref)) continue;
    const refRow = ref as DynamicEntityRow;
    const referensiDocumentId = String(refRow.documentId ?? '').trim();
    if (!referensiDocumentId || seen.has(referensiDocumentId)) continue;
    seen.add(referensiDocumentId);
    const label = referensiPeralatanItemLabel(refRow);
    if (!label) continue;
    out.push({ referensiDocumentId, label });
  }
  return out.sort((a, b) => a.label.localeCompare(b.label, 'id'));
}

export function mergeEquipmentChecklistItems(
  fromPlans: EquipmentChecklistItem[],
  fromSaved: EquipmentChecklistItem[],
): EquipmentChecklistItem[] {
  const map = new Map<string, EquipmentChecklistItem>();
  for (const item of fromPlans) map.set(item.referensiDocumentId, item);
  for (const item of fromSaved) {
    if (!map.has(item.referensiDocumentId)) map.set(item.referensiDocumentId, item);
  }
  return [...map.values()].sort((a, b) => a.label.localeCompare(b.label, 'id'));
}

export function checkedReferensiIdsFromSavedTools(rows: DynamicEntityRow[]): Set<string> {
  const ids = new Set<string>();
  for (const row of rows) {
    const id = relationReferensiDocumentId(row);
    if (id) ids.add(id);
  }
  return ids;
}

function alatDocumentIdFromSavedRow(row: DynamicEntityRow | undefined): string | undefined {
  const alat = row?.alat;
  if (!alat || typeof alat !== 'object' || Array.isArray(alat)) return undefined;
  const id = String((alat as Record<string, unknown>).documentId ?? '').trim();
  return id || undefined;
}

function parseSavedToolCondition(raw: unknown): 1 | 2 | undefined {
  const n = Number(raw);
  if (n === 1 || n === 2) return n;
  return undefined;
}

/** Payload simpan checklist: item tercentang, metadata dari baris tersimpan (pra-survey) jika ada. */
export function buildSamplingCheckToolSaveRowsFromCheckState(
  items: EquipmentChecklistItem[],
  checkState: Record<string, boolean>,
  savedRows: DynamicEntityRow[],
): StrapiSamplingCheckToolSaveRow[] {
  const savedByReferensi = new Map<string, DynamicEntityRow>();
  for (const row of savedRows) {
    const refId = relationReferensiDocumentId(row);
    if (refId) savedByReferensi.set(refId, row);
  }

  const payload: StrapiSamplingCheckToolSaveRow[] = [];
  for (const item of items) {
    if (!checkState[item.referensiDocumentId]) continue;
    const saved = savedByReferensi.get(item.referensiDocumentId);
    const alatDocumentId = alatDocumentIdFromSavedRow(saved);
    const initial = parseSavedToolCondition(saved?.initial_condition) ?? 1;
    const end = parseSavedToolCondition(saved?.end_condition);
    const description = String(saved?.description ?? '').trim();
    payload.push({
      referensiDocumentId: item.referensiDocumentId,
      ...(alatDocumentId ? { alatDocumentId } : {}),
      initial_condition: initial,
      ...(end != null ? { end_condition: end } : {}),
      ...(description ? { description } : {}),
    });
  }
  return payload;
}

export function toolKategoriDocumentId(tool: DynamicEntityRow): string {
  const kat = tool.kategori_referensi;
  if (!kat || typeof kat !== 'object' || Array.isArray(kat)) return '';
  return String((kat as Record<string, unknown>).documentId ?? '').trim();
}

export function toolDisplayLabel(tool: DynamicEntityRow): string {
  const nama = rowString(tool, 'nama_alat');
  const inv = rowString(tool, 'nomor_inventaris_alat');
  return [nama, inv].filter(Boolean).join(' · ') || tool.documentId;
}
